/* ------------------------------------------------------------------
   PLU email popup
   Native, app-free email capture overlay. Paired with plu-popup.css.
   ------------------------------------------------------------------ */

(function () {
  'use strict';

  if (typeof window === 'undefined' || !window.customElements) return;
  if (window.customElements.get('plu-popup')) return;

  var KEY_SIGNED_UP = 'plu:popup:signed-up';
  var KEY_CLOSED_AT = 'plu:popup:closed-at';
  var KEY_GEO = 'plu:geo';
  var KEY_TEASER_HIDDEN = 'plu:popup:teaser-hidden';
  var KEY_PENDING_REGION = 'plu:popup:region';
  var KEY_PENDING_EMAIL = 'plu:popup:email';
  var KEY_PENDING_PHONE = 'plu:popup:phone';
  var KEY_SID = 'plu:popup:sid';

  var ANCHORED_CLASS = 'plu-popup--anchored';

  function readStore(store, key) {
    try {
      return window[store].getItem(key);
    } catch (error) {
      return null;
    }
  }

  function writeStore(store, key, value) {
    try {
      window[store].setItem(key, value);
    } catch (error) {
      /* storage unavailable, fail quietly */
    }
  }

  function removeStore(store, key) {
    try {
      window[store].removeItem(key);
    } catch (error) {
      /* storage unavailable, fail quietly */
    }
  }

  function escapeHtml(value) {
    return String(value == null ? '' : value)
      .split('&').join('&amp;')
      .split('<').join('&lt;')
      .split('>').join('&gt;')
      .split('"').join('&quot;');
  }

  function replaceAll(subject, needle, replacement) {
    return String(subject).split(needle).join(replacement);
  }

  function noop() {}

  /* ?plu_popup_preview=us|intl forces that region open, ignores the
     frequency cap and records no analytics. Same switch the app embed
     honours, so a tester never has to wait out the 7 day suppression. */
  function previewParam() {
    var match = /[?&]plu_popup_preview=(us|intl)(?:&|#|$)/.exec(window.location.search || '');
    return match ? match[1] : '';
  }

  function coarsePointer() {
    return !!(window.matchMedia && window.matchMedia('(pointer: coarse)').matches);
  }

  var PluPopup = class extends HTMLElement {};

  PluPopup.prototype.connectedCallback = function () {
    if (this.pluInitialised) return;
    this.pluInitialised = true;

    this.dialog = this.querySelector('.plu-popup__dialog');
    this.regionElements = Array.prototype.slice.call(this.querySelectorAll('.plu-popup__region'));
    this.teaser = this.querySelector('[data-plu-teaser]');

    if (!this.dialog || this.regionElements.length === 0) return;

    this.sectionId = this.dataset.sectionId || '';
    this.pageType = this.dataset.pageType || '';
    this.triggerMode = this.dataset.trigger || 'delay';
    this.delaySeconds = parseFloat(this.dataset.delay || '5');
    this.scrollThreshold = parseFloat(this.dataset.scroll || '40');
    this.suppressDays = parseFloat(this.dataset.suppressDays || '7');
    this.previewRegion = this.dataset.previewRegion || 'auto';
    this.previewParam = previewParam();
    if (this.previewParam) this.previewRegion = this.previewParam;
    this.doubleOptIn = this.dataset.doubleOptIn === 'true';
    this.showTeaserSetting = this.dataset.showTeaser === 'true';
    this.analyticsEndpoint = this.dataset.analyticsEndpoint || '';
    this.subscribeEndpoint = this.dataset.subscribeEndpoint || '';
    this.designMode = !!(window.Shopify && window.Shopify.designMode);
    this.state = 'form';
    this.triggered = false;
    this.firedBy = '';
    this.viewSent = false;
    this.timers = [];
    this.anchorHandler = null;

    this.bindEvents();
    this.start();
  };

  PluPopup.prototype.disconnectedCallback = function () {
    var self = this;

    (this.timers || []).forEach(function (timer) {
      window.clearTimeout(timer);
    });
    this.timers = [];

    this.unbindAnchor();

    if (this.scrollHandler) {
      window.removeEventListener('scroll', this.scrollHandler);
      this.scrollHandler = null;
    }

    if (this.exitHandler) {
      document.removeEventListener('mouseleave', this.exitHandler);
      document.removeEventListener('mouseout', this.exitHandler);
      this.exitHandler = null;
    }

    if (this.editorHandlers) {
      Object.keys(this.editorHandlers).forEach(function (name) {
        document.removeEventListener(name, self.editorHandlers[name]);
      });
      this.editorHandlers = null;
    }
  };

  /* ---------- Setup ---------- */

  PluPopup.prototype.bindEvents = function () {
    var self = this;

    this.dialog.addEventListener('close', function () {
      self.onDialogClose();
    });

    this.dialog.addEventListener('click', function (event) {
      if (event.target === self.dialog) self.close();
    });

    this.addEventListener('click', function (event) {
      var target = event.target;
      if (!target || !target.closest) return;

      if (target.closest('[data-plu-teaser-close]')) {
        event.preventDefault();
        event.stopPropagation();
        writeStore('sessionStorage', KEY_TEASER_HIDDEN, '1');
        if (self.teaser) self.teaser.hidden = true;
        return;
      }

      if (target.closest('[data-plu-teaser]')) {
        event.preventDefault();
        self.beacon('teaser_open');
        self.open('form');
        return;
      }

      if (target.closest('[data-plu-close]') || target.closest('[data-plu-dismiss]')) {
        event.preventDefault();
        self.close();
        return;
      }

      if (target.closest('[data-plu-copy]')) {
        event.preventDefault();
        self.copyCode(target.closest('[data-plu-copy]'));
      }
    });

    this.addEventListener('submit', function (event) {
      self.onSubmit(event);
    });
  };

  PluPopup.prototype.start = function () {
    var self = this;

    this.resolveRegion().then(function (result) {
      self.applyRegion(result.region, result.handle);
      self.afterRegion();
    }).catch(function () {
      var fallback = self.regionForHandle('US');
      self.applyRegion(fallback, 'US');
      self.afterRegion();
    });
  };

  PluPopup.prototype.afterRegion = function () {
    if (this.designMode) {
      // The editor fires shopify:section:select on its own, including on load
      // when the section is already selected, so never auto-open here.
      this.bindEditorEvents();
      return;
    }

    // The footer newsletter is also a {% form 'customer' %}, so ?customer_posted=true
    // can come from a signup that has nothing to do with this popup. Only treat the
    // "posted" attribute as ours when onSubmit stored a pending region first.
    if (this.hasAttribute('posted') && readStore('sessionStorage', KEY_PENDING_REGION)) {
      this.onPosted();
      return;
    }

    removeStore('sessionStorage', KEY_PENDING_REGION);
    removeStore('sessionStorage', KEY_PENDING_EMAIL);
    removeStore('sessionStorage', KEY_PENDING_PHONE);

    if (this.previewParam) {
      this.open('form');
      return;
    }

    if (this.isSuppressed()) {
      this.maybeShowTeaser();
      return;
    }

    this.armTrigger();
  };

  /* ---------- Analytics ---------- */

  PluPopup.prototype.sessionId = function () {
    var sid = readStore('sessionStorage', KEY_SID);
    if (sid) return sid;

    try {
      if (window.crypto && typeof window.crypto.randomUUID === 'function') {
        sid = window.crypto.randomUUID();
      }
    } catch (error) {
      sid = null;
    }

    if (!sid) {
      sid = 'plu-' + Math.random().toString(36).slice(2) + '-' + Date.now().toString(36);
    }

    writeStore('sessionStorage', KEY_SID, sid);

    return sid;
  };

  PluPopup.prototype.beacon = function (eventName) {
    if (this.designMode) return;
    if (this.previewParam) return;
    if (!this.analyticsEndpoint) return;

    try {
      if (!navigator.sendBeacon) return;

      var isMobile = !!(window.matchMedia && window.matchMedia('(max-width: 899px)').matches);

      navigator.sendBeacon(this.analyticsEndpoint, JSON.stringify({
        event: eventName,
        region: this.region || '',
        country: this.geoHandle || '',
        device: isMobile ? 'mobile' : 'desktop',
        trigger: this.firedBy || this.triggerMode,
        pageType: this.pageType,
        path: window.location.pathname,
        sid: this.sessionId()
      }));
    } catch (error) {
      /* beacon unavailable, fail quietly */
    }
  };

  /* ---------- Region resolution ---------- */

  PluPopup.prototype.availableRegions = function () {
    return this.regionElements.map(function (element) {
      return element.dataset.region;
    });
  };

  PluPopup.prototype.regionForHandle = function (handle) {
    var wanted = handle === 'US' ? 'us' : 'intl';
    var available = this.availableRegions();
    if (available.indexOf(wanted) !== -1) return wanted;
    return available[0];
  };

  PluPopup.prototype.resolveRegion = function () {
    var self = this;

    if (this.previewRegion === 'us' || this.previewRegion === 'intl') {
      return Promise.resolve({
        region: this.previewRegion,
        handle: this.previewRegion === 'us' ? 'US' : 'INTL'
      });
    }

    var pending = readStore('sessionStorage', KEY_PENDING_REGION);
    var cached = readStore('sessionStorage', KEY_GEO);

    if (cached) {
      return Promise.resolve({
        region: pending || this.regionForHandle(cached),
        handle: cached
      });
    }

    return fetch('/browsing_context_suggestions.json?source=geolocation_recommendation', {
      credentials: 'same-origin'
    }).then(function (response) {
      return response.json();
    }).then(function (data) {
      var handle = 'US';

      if (data && data.detected_values && data.detected_values.country && data.detected_values.country.handle) {
        handle = data.detected_values.country.handle;
      }

      writeStore('sessionStorage', KEY_GEO, handle);

      return {
        region: pending || self.regionForHandle(handle),
        handle: handle
      };
    }).catch(function () {
      return {
        region: pending || self.regionForHandle('US'),
        handle: 'US'
      };
    });
  };

  PluPopup.prototype.applyRegion = function (region, handle) {
    var self = this;
    var resolved = region;
    var available = this.availableRegions();

    if (available.indexOf(resolved) === -1) resolved = available[0];

    this.region = resolved;
    this.geoHandle = handle || 'US';

    this.regionElements.forEach(function (element) {
      var matches = element.dataset.region === resolved;
      element.hidden = !matches;

      if (!matches) return;

      var tags = element.querySelector('[data-plu-tags]');

      if (tags) {
        var base = tags.dataset.pluBaseTags || tags.value || '';
        tags.value = base + ',geo:' + self.geoHandle;
      }
    });

    if (this.teaser) {
      Array.prototype.slice.call(this.teaser.querySelectorAll('.plu-popup__teaser-text')).forEach(function (element) {
        element.hidden = element.dataset.region !== resolved;
      });
    }
  };

  PluPopup.prototype.currentRegionElement = function () {
    var self = this;
    var match = null;

    this.regionElements.forEach(function (element) {
      if (element.dataset.region === self.region) match = element;
    });

    return match || this.regionElements[0];
  };

  /* ---------- Suppression ---------- */

  PluPopup.prototype.hasSignedUp = function () {
    return readStore('localStorage', KEY_SIGNED_UP) === '1';
  };

  PluPopup.prototype.isSuppressed = function () {
    if (this.hasSignedUp()) return true;

    var closedAt = parseInt(readStore('localStorage', KEY_CLOSED_AT) || '0', 10);
    if (!closedAt) return false;

    var days = isNaN(this.suppressDays) ? 7 : this.suppressDays;
    return Date.now() - closedAt < days * 24 * 60 * 60 * 1000;
  };

  /* ---------- Triggers ---------- */

  PluPopup.prototype.armTrigger = function () {
    var self = this;
    var mode = this.triggerMode;

    /* Time delay or scroll depth, whichever comes first. Both halves are
       armed and fireTrigger() tears down whichever is still waiting. The
       scroll half only reacts to real scroll events, so a short page that
       is already half visible on load waits for the timer instead of
       opening at once. */
    if (mode === 'delay_or_scroll') {
      var depth = isNaN(this.scrollThreshold) ? 50 : this.scrollThreshold;
      var wait = isNaN(this.delaySeconds) ? 30 : this.delaySeconds;

      this.scrollHandler = function () {
        var docHeight = document.documentElement.scrollHeight || 1;
        var progress = ((window.scrollY + window.innerHeight) / docHeight) * 100;

        if (progress >= depth) self.fireTrigger('scroll');
      };

      window.addEventListener('scroll', this.scrollHandler, { passive: true });

      this.timers.push(window.setTimeout(function () {
        self.fireTrigger('delay');
      }, wait * 1000));
      return;
    }

    if (mode === 'scroll') {
      var threshold = isNaN(this.scrollThreshold) ? 40 : this.scrollThreshold;

      this.scrollHandler = function () {
        var docHeight = document.documentElement.scrollHeight || 1;
        var progress = ((window.scrollY + window.innerHeight) / docHeight) * 100;

        if (progress >= threshold) {
          window.removeEventListener('scroll', self.scrollHandler);
          self.scrollHandler = null;
          self.fireTrigger();
        }
      };

      window.addEventListener('scroll', this.scrollHandler, { passive: true });
      this.scrollHandler();
      return;
    }

    if (mode === 'exit') {
      var finePointer = !!(window.matchMedia && window.matchMedia('(pointer: fine)').matches);

      if (finePointer) {
        this.exitHandler = function (event) {
          var leaving = event.type === 'mouseleave'
            ? event.clientY <= 0
            : (!event.relatedTarget && event.clientY <= 0);

          if (!leaving) return;

          document.removeEventListener('mouseleave', self.exitHandler);
          document.removeEventListener('mouseout', self.exitHandler);
          self.exitHandler = null;
          self.fireTrigger();
        };

        document.addEventListener('mouseleave', this.exitHandler);
        document.addEventListener('mouseout', this.exitHandler);
        return;
      }
    }

    var seconds = isNaN(this.delaySeconds) ? 5 : this.delaySeconds;

    this.timers.push(window.setTimeout(function () {
      self.fireTrigger();
    }, seconds * 1000));
  };

  PluPopup.prototype.fireTrigger = function (source) {
    if (this.triggered) return;
    if (this.hasSignedUp()) return;

    this.triggered = true;
    if (source) this.firedBy = source;

    if (this.scrollHandler) {
      window.removeEventListener('scroll', this.scrollHandler);
      this.scrollHandler = null;
    }

    (this.timers || []).forEach(function (timer) {
      window.clearTimeout(timer);
    });
    this.timers = [];

    this.open('form');
  };

  /* ---------- Open / close ---------- */

  PluPopup.prototype.setState = function (state) {
    var regionElement = this.currentRegionElement();
    if (!regionElement) return;

    this.state = state;

    Array.prototype.slice.call(regionElement.querySelectorAll('.plu-popup__state')).forEach(function (element) {
      element.hidden = element.dataset.state !== state;
    });
  };

  PluPopup.prototype.isOpen = function () {
    return !!(this.dialog.open || this.dialog.hasAttribute('open'));
  };

  PluPopup.prototype.open = function (state) {
    if (!this.dialog) return;

    this.setState(state || 'form');

    if (!this.isOpen()) {
      /* Touch devices never rely on position: fixed for the open dialog.
         In-app browsers (Instagram's above all) can draw a fixed modal
         against the top of the document when the shopper has already
         scrolled, leaving a locked page with nothing visible on it. The
         dialog is placed in document coordinates over the visual viewport
         instead, and follows it if the page still moves. Anchoring comes
         first because showModal() moves focus into the card and a browser
         may scroll to wherever the card is at that moment. */
      var anchored = coarsePointer();
      if (anchored) this.anchor();

      try {
        this.dialog.showModal();
      } catch (error) {
        this.dialog.setAttribute('open', '');
      }

      if (anchored) this.bindAnchor();

      if (!this.viewSent) {
        this.viewSent = true;
        this.beacon('view');
      }
    }

    document.body.classList.add('plu-popup-open');

    if (this.teaser) this.teaser.hidden = true;

    if ((state || 'form') === 'form') {
      var regionElement = this.currentRegionElement();
      var email = regionElement ? regionElement.querySelector('input[type="email"]') : null;

      if (email) {
        try {
          email.focus({ preventScroll: true });
        } catch (error) {
          email.focus();
        }
      }
    }
  };

  /* ---------- Anchoring (touch devices) ---------- */

  PluPopup.prototype.anchor = function () {
    var dialog = this.dialog;
    var viewport = window.visualViewport;
    var doc = document.documentElement;

    var top = viewport ? viewport.pageTop : (window.pageYOffset || doc.scrollTop || 0);
    var left = viewport ? viewport.pageLeft : (window.pageXOffset || doc.scrollLeft || 0);
    var height = viewport ? viewport.height : (window.innerHeight || doc.clientHeight || 0);
    var width = viewport ? viewport.width : (window.innerWidth || doc.clientWidth || 0);

    if (!(height > 0) || !(width > 0)) return;

    top = Math.max(0, Math.round(top));
    left = Math.max(0, Math.round(left));

    /* The layout box (top, left) is placed once per open, in document
       coordinates as before. If the visual viewport moves afterwards, the
       dialog follows it with a transform: transform changes are not layout
       shifts, while rewriting top and left on every scroll counted as one
       each time and made the popup the main mobile CLS source. */
    if (!dialog.classList.contains(ANCHORED_CLASS)) {
      dialog.classList.add(ANCHORED_CLASS);
      this.anchorTop = top;
      this.anchorLeft = left;
      dialog.style.top = top + 'px';
      dialog.style.left = left + 'px';
    }

    var dx = left - this.anchorLeft;
    var dy = top - this.anchorTop;
    dialog.style.transform = (dx || dy) ? 'translate3d(' + dx + 'px, ' + dy + 'px, 0)' : '';
    dialog.style.width = Math.round(width) + 'px';
    dialog.style.height = Math.round(height) + 'px';
  };

  PluPopup.prototype.unanchor = function () {
    this.unbindAnchor();

    if (!this.dialog) return;

    this.dialog.classList.remove(ANCHORED_CLASS);
    this.dialog.style.transform = '';
    this.dialog.style.top = '';
    this.dialog.style.left = '';
    this.dialog.style.width = '';
    this.dialog.style.height = '';
  };

  PluPopup.prototype.bindAnchor = function () {
    var self = this;
    var pending = false;

    if (this.anchorHandler) return;

    var run = function () {
      pending = false;
      if (self.isOpen()) self.anchor();
    };

    this.anchorHandler = function () {
      if (pending) return;
      pending = true;

      if (typeof window.requestAnimationFrame === 'function') {
        window.requestAnimationFrame(run);
      } else {
        window.setTimeout(run, 16);
      }
    };

    window.addEventListener('scroll', this.anchorHandler, { passive: true });
    window.addEventListener('resize', this.anchorHandler);
    window.addEventListener('orientationchange', this.anchorHandler);

    if (window.visualViewport) {
      window.visualViewport.addEventListener('scroll', this.anchorHandler);
      window.visualViewport.addEventListener('resize', this.anchorHandler);
    }
  };

  PluPopup.prototype.unbindAnchor = function () {
    if (!this.anchorHandler) return;

    window.removeEventListener('scroll', this.anchorHandler);
    window.removeEventListener('resize', this.anchorHandler);
    window.removeEventListener('orientationchange', this.anchorHandler);

    if (window.visualViewport) {
      window.visualViewport.removeEventListener('scroll', this.anchorHandler);
      window.visualViewport.removeEventListener('resize', this.anchorHandler);
    }

    this.anchorHandler = null;
  };

  PluPopup.prototype.close = function () {
    if (!this.dialog) return;

    if (this.dialog.open) {
      this.dialog.close();
    } else {
      this.onDialogClose();
    }
  };

  PluPopup.prototype.onDialogClose = function () {
    document.body.classList.remove('plu-popup-open');
    this.dialog.removeAttribute('open');
    this.unanchor();
    this.viewSent = false;

    if (this.state === 'form') {
      this.beacon('close');
    }

    if (!this.designMode && !this.previewParam && this.state === 'form') {
      writeStore('localStorage', KEY_CLOSED_AT, String(Date.now()));
    }

    if (!this.designMode) this.maybeShowTeaser();
  };

  PluPopup.prototype.maybeShowTeaser = function () {
    if (!this.teaser) return;
    if (!this.showTeaserSetting) return;
    if (this.hasSignedUp()) return;
    if (readStore('sessionStorage', KEY_TEASER_HIDDEN) === '1') return;
    if (this.dialog && this.isOpen()) return;

    this.teaser.hidden = false;
  };

  /* ---------- Post-redirect success ---------- */

  PluPopup.prototype.onPosted = function () {
    var email = readStore('sessionStorage', KEY_PENDING_EMAIL) || '';
    var phone = readStore('sessionStorage', KEY_PENDING_PHONE) || '';

    writeStore('localStorage', KEY_SIGNED_UP, '1');
    this.fillTokens(email);
    this.open(this.doubleOptIn ? 'confirm' : 'success');
    this.beacon('signup');
    this.sendSubscribe(email, phone, this.region);
    this.cleanUrl();

    removeStore('sessionStorage', KEY_PENDING_REGION);
    removeStore('sessionStorage', KEY_PENDING_EMAIL);
    removeStore('sessionStorage', KEY_PENDING_PHONE);
  };

  PluPopup.prototype.cleanUrl = function () {
    try {
      var url = new URL(window.location.href);

      if (!url.searchParams.has('customer_posted')) return;

      url.searchParams.delete('customer_posted');
      window.history.replaceState({}, '', url.toString());
    } catch (error) {
      /* history unavailable, fail quietly */
    }
  };

  PluPopup.prototype.fillTokens = function (email) {
    var regionElement = this.currentRegionElement();
    if (!regionElement) return;

    var reward = regionElement.dataset.reward || '';
    var emailText = email || 'your email address';

    Array.prototype.slice.call(regionElement.querySelectorAll('[data-plu-tokens]')).forEach(function (element) {
      if (typeof element.pluTemplate !== 'string') element.pluTemplate = element.innerHTML;

      var output = replaceAll(element.pluTemplate, '[email]', escapeHtml(emailText));
      output = replaceAll(output, '[reward]', escapeHtml(reward));
      element.innerHTML = output;
    });
  };

  /* ---------- Submit ---------- */

  PluPopup.prototype.showError = function (form, message) {
    var box = form.querySelector('[data-plu-error]');
    if (!box) return;

    box.textContent = message;
    box.hidden = !message;
  };

  PluPopup.prototype.onSubmit = function (event) {
    var self = this;
    var form = event.target;

    if (!form || form.tagName !== 'FORM') return;

    var regionElement = form.closest ? form.closest('.plu-popup__region') : null;
    if (!regionElement || regionElement.hidden) return;

    event.preventDefault();

    var button = form.querySelector('button[type="submit"]');
    var emailInput = form.querySelector('input[type="email"]');
    var phoneInput = form.querySelector('[name="plu_phone"]');
    var email = emailInput ? emailInput.value : '';
    var phone = phoneInput ? phoneInput.value.trim() : '';
    var regionName = regionElement.dataset.region || this.region;

    this.showError(form, '');
    if (button) button.disabled = true;

    writeStore('sessionStorage', KEY_PENDING_EMAIL, email);
    writeStore('sessionStorage', KEY_PENDING_PHONE, phone);

    this.beacon('submit');

    var nameInput = form.querySelector('[name="contact[first_name]"]');
    var hpInput = form.querySelector('[data-plu-hp]');
    var tagsInput = form.querySelector('[data-plu-tags]');
    var rawTags = tagsInput ? (tagsInput.value || '').split(',') : [];
    var tags = [];
    for (var i = 0; i < rawTags.length; i++) {
      var tag = rawTags[i].replace(/^\s+|\s+$/g, '');
      if (tag) tags.push(tag);
    }

    var fail = function (message) {
      self.showError(form, message || 'Something went wrong. Please try again.');
      if (button) button.disabled = false;
    };

    // v2: the signup goes to the app proxy, never to Shopify's /contact form
    // (the storefront captcha rejects fetch posts and redirects native ones).
    if (!this.subscribeEndpoint) {
      fail();
      return;
    }

    fetch(this.subscribeEndpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'text/plain' },
      credentials: 'same-origin',
      body: JSON.stringify({
        email: email,
        phone: phone,
        first_name: nameInput ? nameInput.value.replace(/^\s+|\s+$/g, '') : '',
        region: regionName || '',
        country: this.geoHandle || '',
        tags: tags,
        hp: hpInput ? hpInput.value : ''
      })
    }).then(function (response) {
      return response.json();
    }).then(function (result) {
      if (result && result.ok) {
        self.onSuccess(regionName, email, phone);
        return;
      }
      fail(result && result.message);
    }).catch(function () {
      fail();
    });
  };

  PluPopup.prototype.looksSuccessful = function (result) {
    if (result.url && result.url.indexOf('customer_posted=true') !== -1) return true;
    if (result.text && result.text.indexOf('customer_posted') !== -1) return true;

    try {
      var doc = new DOMParser().parseFromString(result.text, 'text/html');
      if (doc.querySelector('plu-popup[posted]')) return true;
    } catch (error) {
      /* parsing failed, treat as not successful */
    }

    return false;
  };

  PluPopup.prototype.extractError = function (html, formId) {
    var fallback = 'Something went wrong. Please try again.';

    try {
      var doc = new DOMParser().parseFromString(html || '', 'text/html');

      if (formId) {
        var escaped = window.CSS && window.CSS.escape ? window.CSS.escape(formId) : formId;
        var scoped = doc.querySelector('#' + escaped + ' [data-plu-error]');

        if (scoped && scoped.textContent.trim()) return scoped.textContent.trim();
      }

      var generic = doc.querySelector('[data-plu-error]');
      if (generic && generic.textContent.trim()) return generic.textContent.trim();
    } catch (error) {
      /* parsing failed, use the fallback */
    }

    return fallback;
  };

  // Runs on every successful signup, with or without a phone number: the app
  // fires the Shopify Flow trigger from here, and attaches SMS consent only
  // when a phone came along.
  PluPopup.prototype.sendSubscribe = function (email, phone, regionName) {
    if (!this.subscribeEndpoint) return;
    if (!email) return;

    try {
      fetch(this.subscribeEndpoint, {
        method: 'POST',
        keepalive: true,
        headers: { 'Content-Type': 'text/plain' },
        body: JSON.stringify({
          email: email,
          phone: phone || '',
          region: regionName || this.region || '',
          country: this.geoHandle || ''
        })
      }).catch(noop);
    } catch (error) {
      /* endpoint unreachable, never block the success state */
    }
  };

  PluPopup.prototype.onSuccess = function (regionName, email, phone) {
    writeStore('localStorage', KEY_SIGNED_UP, '1');

    this.dispatchEvent(new CustomEvent('plu:popup:signup', {
      detail: { region: regionName, email: email },
      bubbles: true
    }));

    if (window.dataLayer && typeof window.dataLayer.push === 'function') {
      window.dataLayer.push({ event: 'plu_popup_signup', region: regionName });
    }

    this.beacon('signup');

    this.fillTokens(email);
    this.setState(this.doubleOptIn ? 'confirm' : 'success');

    removeStore('sessionStorage', KEY_PENDING_EMAIL);
    removeStore('sessionStorage', KEY_PENDING_PHONE);

    if (this.teaser) this.teaser.hidden = true;
  };

  /* ---------- Copy code ---------- */

  PluPopup.prototype.copyCode = function (button) {
    if (!button) return;

    var wrapper = button.closest('.plu-popup__code');
    var input = wrapper ? wrapper.querySelector('[data-plu-code]') : null;
    if (!input) return;

    var original = button.dataset.pluCopyLabel || button.textContent;
    button.dataset.pluCopyLabel = original;

    var done = function () {
      button.textContent = 'Copied';
      window.setTimeout(function () {
        button.textContent = original;
      }, 2000);
    };

    var legacy = function () {
      try {
        input.select();
        document.execCommand('copy');
        done();
      } catch (error) {
        /* copy unavailable, leave the code visible for manual selection */
      }
    };

    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(input.value).then(done).catch(legacy);
      return;
    }

    legacy();
  };

  /* ---------- Theme editor ---------- */

  PluPopup.prototype.bindEditorEvents = function () {
    var self = this;

    var isMine = function (event) {
      return !!(event.detail && String(event.detail.sectionId) === String(self.sectionId));
    };

    this.editorHandlers = {
      'shopify:section:select': function (event) {
        if (isMine(event)) self.open('form');
      },
      'shopify:section:deselect': function (event) {
        if (isMine(event)) self.close();
      },
      'shopify:block:select': function (event) {
        if (!isMine(event)) return;

        var blockId = event.detail ? event.detail.blockId : null;
        var match = null;

        self.regionElements.forEach(function (element) {
          if (String(element.dataset.blockId) === String(blockId)) match = element;
        });

        if (match) self.applyRegion(match.dataset.region, self.geoHandle);

        self.open('form');
      }
    };

    Object.keys(this.editorHandlers).forEach(function (name) {
      document.addEventListener(name, self.editorHandlers[name]);
    });
  };

  window.customElements.define('plu-popup', PluPopup);
})();
